import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    Chip,
    Box,
    Typography,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    MenuItem,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Alert,
    LinearProgress,
    IconButton,
    Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import PendingIcon from '@mui/icons-material/Pending';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import RunProgressIndicator from './RunProgressIndicator';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Dialog component to display run history for Autodesk Docs copy operations
 * Shows list of runs with expandable details for each run
 */
const RunHistoryDialog = ({ open, onClose, flowId, srcProjectId, dstProjectId, srcProjectName, dstProjectName }) => {
    const [runHistory, setRunHistory] = useState([]);
    const [runDetails, setRunDetails] = useState({});
    const [searchQueries, setSearchQueries] = useState({}); // Track search query per run
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRun, setExpandedRun] = useState(null);
    const [pollingInterval, setPollingInterval] = useState(null);
    const abortControllerRef = useRef(null);

    // Reset state when dialog is opened with different props
    useEffect(() => {
        if (open) {
            // Clear previous data when switching to a different mapping
            setRunHistory([]);
            setRunDetails({});
            setSearchQueries({});
            setExpandedRun(null);
            setError(null);
            setLoading(true); // Set loading to true when starting a new fetch

            // Fetch new data
            fetchRunHistory();
        } else {
            // Clear data when dialog closes
            setRunHistory([]);
            setRunDetails({});
            setSearchQueries({});
            setExpandedRun(null);
            setError(null);
            setLoading(true); // Reset loading state for next open
        }

        return () => {
            // Cleanup on unmount or when dependencies change
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null; // Clear the ref after aborting
            }
            if (pollingInterval) {
                clearInterval(pollingInterval);
                setPollingInterval(null); // Clear the interval state
            }
        };
    }, [open, flowId, srcProjectId, dstProjectId]); // Re-run when any of these change

    // Fetch run history
    const fetchRunHistory = async () => {
        if (!flowId || !srcProjectId || !dstProjectId) {
            setLoading(false);
            return;
        }

        // Cancel any ongoing request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new AbortController for this request
        abortControllerRef.current = new AbortController();

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${API_BASE_URL}synctrack/autodesk/run-history/runs?flowId=${flowId}&srcProjectId=${srcProjectId}&dstProjectId=${dstProjectId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    },
                    signal: abortControllerRef.current.signal
                }
            );

            if (!response.ok) {
                const errorText = await response.text().catch(() => '');
                throw new Error(`HTTP ${response.status}: ${response.statusText}${errorText ? ' - ' + errorText : ''}`);
            }

            const data = await response.json();
            setRunHistory(data);
            setError(null);

            // Check if any runs are still running
            const hasRunningOps = data.some(run => run.running);

            // Set up polling if there are running operations (every 10 seconds)
            if (hasRunningOps && !pollingInterval) {
                const interval = setInterval(fetchRunHistory, 10000); // Changed from 5000 to 10000
                setPollingInterval(interval);
            } else if (!hasRunningOps && pollingInterval) {
                clearInterval(pollingInterval);
                setPollingInterval(null);
            }

        } catch (err) {
            // Ignore abort errors
            if (err.name === 'AbortError') {
                console.log('Request cancelled');
                return;
            }

            console.error('Failed to fetch run history:', err);
            setError(err.message);
            setLoading(false);

            // Stop polling on error
            if (pollingInterval) {
                clearInterval(pollingInterval);
                setPollingInterval(null);
            }
        } finally {
            if (!abortControllerRef.current?.signal.aborted) {
                setLoading(false);
            }
        }
    };

    // Fetch run details for a specific run
    const fetchRunDetails = async (runId, force = false) => {
        if (!force && runDetails[runId]) return; // Already loaded, skip unless forced

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${API_BASE_URL}synctrack/autodesk/run-history/details?runId=${runId}&srcProjectId=${srcProjectId}&dstProjectId=${dstProjectId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch run details');
            }

            const data = await response.json();
            setRunDetails(prev => ({ ...prev, [runId]: data }));
        } catch (err) {
            console.error('Failed to fetch run details:', err);
        }
    };

    // Force refresh details for a specific run
    const refreshRunDetails = (runId) => {
        fetchRunDetails(runId, true);
    };

    // Handle run expansion
    const handleExpandRun = (runId) => {
        if (expandedRun === runId) {
            setExpandedRun(null);
        } else {
            setExpandedRun(runId);
            fetchRunDetails(runId);
        }
    };

    // Get status icon
    const getStatusIcon = (status) => {
        const statusKey = status?.toLowerCase();
        if (statusKey === 'success') return <CheckCircleIcon color="success" fontSize="small" />;
        if (statusKey === 'failed') return <ErrorIcon color="error" fontSize="small" />;
        if (statusKey === 'skipped') return <SkipNextIcon color="warning" fontSize="small" />;
        if (statusKey === 'running') return <PendingIcon color="info" fontSize="small" />;
        return null;
    };

    // Format duration
    const formatDuration = (ms) => {
        if (!ms) return 'N/A';
        const seconds = Math.floor(ms / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        if (hours > 0) return `${hours}h ${minutes % 60}m`;
        if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
        return `${seconds}s`;
    };

    // Format date/time
    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        try {
            return new Date(dateTimeString).toLocaleString();
        } catch {
            return dateTimeString;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                Run History
                <Typography variant="caption" display="block" color="textSecondary">
                    {srcProjectName || srcProjectId} → {dstProjectName || dstProjectId}
                </Typography>
            </DialogTitle>

            <DialogContent>
                {loading && runHistory.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : runHistory.length === 0 ? (
                    <Alert severity="info">No run history found for this project mapping</Alert>
                ) : (
                    <List>
                        {runHistory.map((run) => (
                            <Accordion
                                key={run.runId}
                                expanded={expandedRun === run.runId}
                                onChange={() => handleExpandRun(run.runId)}
                                sx={{ mb: 1 }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box display="flex" alignItems="center" width="100%" gap={2} flexWrap="wrap">
                                        {/* Date/Time */}
                                        <Typography variant="body2" sx={{ minWidth: 180 }}>
                                            {formatDateTime(run.startTime)}
                                        </Typography>

                                        {/* Status Chip */}
                                        <Chip
                                            label={run.status}
                                            color={
                                                run.status === 'SUCCESS' ? 'success' :
                                                    run.status === 'RUNNING' ? 'info' :
                                                        run.status === 'FAILED' ? 'error' : 'default'
                                            }
                                            size="small"
                                        />

                                        {/* Duration */}
                                        <Typography variant="caption" color="textSecondary">
                                            Duration: {formatDuration(run.durationMs)}
                                        </Typography>

                                        {/* Spacer */}
                                        <Box flexGrow={1} />

                                        {/* Search Box */}
                                        <TextField
                                            size="small"
                                            placeholder="Search files..."
                                            value={searchQueries[run.runId] || ''}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                setSearchQueries(prev => ({
                                                    ...prev,
                                                    [run.runId]: e.target.value
                                                }));
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ width: 250 }}
                                        />

                                        {/* File Statistics */}
                                        <Box display="flex" gap={1} flexWrap="wrap">
                                            <Chip
                                                icon={<CheckCircleIcon />}
                                                label={run.successCount || 0}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                            />
                                            <Chip
                                                icon={<ErrorIcon />}
                                                label={run.failedCount || 0}
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                            />
                                            <Chip
                                                icon={<SkipNextIcon />}
                                                label={run.skippedCount || 0}
                                                size="small"
                                                color="warning"
                                                variant="outlined"
                                            />
                                        </Box>

                                        {/* Refresh Button */}
                                        <Tooltip title="Refresh file details">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    refreshRunDetails(run.runId);
                                                }}
                                                sx={{ ml: 1 }}
                                            >
                                                <RefreshIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </AccordionSummary>

                                <AccordionDetails>
                                    {/* Show live progress for running operations */}
                                    {run.running && (
                                        <RunProgressIndicator
                                            runId={run.runId}
                                            srcProjectId={srcProjectId}
                                            dstProjectId={dstProjectId}
                                            onComplete={() => fetchRunHistory()}
                                            onProgressUpdate={() => refreshRunDetails(run.runId)}
                                        />
                                    )}

                                    {/* File Details Table */}
                                    {runDetails[run.runId] ? (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>File Name</TableCell>
                                                    <TableCell>Full Path</TableCell>
                                                    <TableCell>Size</TableCell>
                                                    <TableCell>Error</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {runDetails[run.runId]
                                                    .filter(detail => {
                                                        const searchQuery = (searchQueries[run.runId] || '').toLowerCase();
                                                        if (!searchQuery) return true;

                                                        const fileName = (detail.fileName || '').toLowerCase();
                                                        const fullPath = (detail.fullPath || '').toLowerCase();

                                                        return fileName.includes(searchQuery) || fullPath.includes(searchQuery);
                                                    })
                                                    .map((detail, idx) => (
                                                        <TableRow key={idx}>
                                                            <TableCell>{getStatusIcon(detail.status)}</TableCell>
                                                            <TableCell>{detail.fileName}</TableCell>
                                                            <TableCell>
                                                                <Typography variant="caption" title={detail.fullPath}>
                                                                    {detail.fullPath}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="caption">
                                                                    {detail.fileSizeFormatted ||
                                                                        (detail.fileSize ? `${(detail.fileSize / 1024 / 1024).toFixed(2)} MB` : 'N/A')}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                {detail.errorMessage && (
                                                                    <Typography variant="caption" color="error">
                                                                        {detail.errorMessage}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                            </TableBody>
                                        </Table>
                                    ) : (
                                        <Box display="flex" justifyContent="center" p={2}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </List>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default RunHistoryDialog;
